import { useCallback, useEffect, useRef } from 'react';

const TABBABLE = [
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'a[href]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

// Hidden elements refuse focus, so trying each candidate in turn also skips those.
const focusNextAfter = (control) => {
  const container = control.closest('[role="dialog"]') || document.body;
  const following = Array.from(container.querySelectorAll(TABBABLE)).filter(
    (el) =>
      el.tabIndex >= 0 &&
      !control.contains(el) &&
      control.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING
  );
  following.some((el) => {
    el.focus();
    return document.activeElement === el;
  });
};

/**
 * Keeps keyboard focus in the time entry forms' icon row (Project, Tag, $, Member) when a
 * field's dropdown closes. The dropdown's input unmounts on close, which would otherwise
 * drop focus out of the form and send the next Tab back to the top of the dialog.
 *
 * The dropdowns also render below the row, so a plain Tab from one would skip the rest of
 * the row. Tab therefore closes the dropdown and continues from the field's row control.
 *
 * Mark each row control with `data-field-control="<field>"`, spread `fieldRowProps` on the
 * row, and call `closeField(field, reason)` from the Autocomplete's onClose.
 */
const useFieldFocusReturn = (setExpandedFields) => {
  const rowRef = useRef(null);
  const pendingRef = useRef(null);

  // Runs after the commit that swaps the field's icon for its chip, so it finds the one on screen.
  useEffect(() => {
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    const control = rowRef.current?.querySelector(`[data-field-control="${pending.field}"]`);
    if (!control) return;
    if (pending.advance) focusNextAfter(control);
    else control.focus();
  });

  const closeField = useCallback((field, reason) => {
    // A blur means the member already moved elsewhere; pulling focus back would undo that.
    if (reason !== 'blur') pendingRef.current = { field, advance: false };
    setExpandedFields((prev) => ({ ...prev, [field]: false }));
  }, [setExpandedFields]);

  const fieldTabKeyDown = useCallback((field) => (event) => {
    if (event.key !== 'Tab') return;
    event.preventDefault();
    // Shift+Tab stops on the field's own control, the nearest thing before the dropdown's input.
    pendingRef.current = { field, advance: !event.shiftKey };
    setExpandedFields((prev) => ({ ...prev, [field]: false }));
  }, [setExpandedFields]);

  const fieldRowProps = {
    ref: rowRef,
    // Chip blurs itself when Escape is released. That Escape closed the dropdown and moved
    // focus onto the chip, so without this the chip would lose focus straight away.
    onKeyUpCapture: (event) => {
      if (event.key === 'Escape') event.stopPropagation();
    },
  };

  return { fieldRowProps, closeField, fieldTabKeyDown };
};

export default useFieldFocusReturn;
